import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ColumnDef } from '@tanstack/react-table';
import i18n from 'i18next';

import { Tag } from '@/components/generic';
import { TableData, User } from '@/types';

const OrgPopup = ({
  orgs,
  anchorRect,
  onClose
}: {
  orgs: Array<{ id?: number; name: string }>;
  anchorRect: DOMRect | null;
  onClose: () => void;
}) => {
  if (!anchorRect) {
    return null;
  }
  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: anchorRect.bottom + 4,
        left: anchorRect.left,
        zIndex: 9999
      }}
      className='min-w-[140px] rounded-lg bg-primary-gray p-2 text-xs text-secondary-steel-blue shadow-lg'
      onMouseLeave={onClose}>
      {orgs.map((org, idx) => (
        <div key={org.id ?? idx} className='px-2 py-1'>
          {`${idx + 1}. ${org.name}`}
        </div>
      ))}
    </div>,
    document.body
  );
};

export const getUserColumns: () => ColumnDef<TableData>[] = () => [
  {
    id: 'name',
    header: i18n.t('fullName', { ns: 'common' }),
    cell: (props) => {
      const user = props.row.original as User;
      return `${user.firstName} ${user.lastName}`;
    }
  },
  {
    id: 'email',
    header: i18n.t('email', { ns: 'common' }),
    accessorKey: 'email'
  },
  {
    id: 'role',
    header: i18n.t('role', { ns: 'common' }),
    accessorKey: 'role.name',
    cell: (props) => (
      <div className='flex justify-end md:justify-start'>{props.getValue() as string}</div>
    )
  },
  {
    id: 'organizations',
    header: i18n.t('organizations', { ns: 'common' }),
    accessorKey: 'organizations',
    cell: (props) => {
      const orgs = (props.row.original as User)?.organizations ?? [];
      const [showPopup, setShowPopup] = useState(false);
      const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
      const extraRef = useRef<HTMLDivElement>(null);
      if (!orgs.length) {
        return null;
      }
      const firstOrg = orgs[0]?.name;
      const extraCount = orgs.length - 1;
      const handleMouseEnter = () => {
        if (extraRef.current) {
          setAnchorRect(extraRef.current.getBoundingClientRect());
        }
        setShowPopup(true);
      };
      const handleMouseLeave = () => {
        setShowPopup(false);
      };
      return (
        <div className='relative flex items-center'>
          <span className='text-sm'>{firstOrg}</span>
          {extraCount > 0 && (
            <div
              ref={extraRef}
              className='ml-2 cursor-pointer rounded-lg bg-primary-gray px-2 text-xs text-primary-blue'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              style={{ position: 'relative', zIndex: 1 }}>
              +{extraCount}
              {showPopup && (
                <OrgPopup orgs={orgs.slice(1)} anchorRect={anchorRect} onClose={handleMouseLeave} />
              )}
            </div>
          )}
        </div>
      );
    }
  },
  {
    id: 'enabled',
    header: i18n.t('status', { ns: 'common' }),
    cell: (props) => {
      const user = props.row.original as User;
      return (
        <Tag
          value={
            user.registeredAt === undefined ? 'Unregistered' : user.enabled ? 'Active' : 'Inactive'
          }
        />
      );
    }
  }
];
